import { Plus, RefreshCw, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import type { UserRow } from "@/components/settings/settings-types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/client/api";
import {
  createUserSchema,
  getCreateUserValidationCode,
  minimumUserPasswordLength,
  updateUserSchema,
  type CreateUserValidationCode,
} from "@/lib/users/user-input";

type UsersResponse = {
  users: UserRow[];
};

type UserDraft = {
  name: string;
  role: "admin" | "staff";
  accountType: "internal" | "external";
  jobTitle: string;
  status: "active" | "disabled";
  password: string;
};

type CreateUserForm = {
  name: string;
  email: string;
  password: string;
  role: "admin" | "staff";
  accountType: "internal" | "external";
  jobTitle: string;
};

const emptyCreateForm: CreateUserForm = {
  name: "",
  email: "",
  password: "",
  role: "staff",
  accountType: "internal",
  jobTitle: "Staff",
};

export function UsersSettings() {
  const { language } = useAppSettings();
  const { user: currentUser } = useAuth();
  const text = language === "zh" ? zh : en;
  const [users, setUsers] = useState<UserRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, UserDraft>>({});
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [trash, setTrash] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadUsers() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<UsersResponse>(trash ? "/api/users?trash=1" : "/api/users");
      setUsers(response.users);
      setDrafts(
        Object.fromEntries(
          response.users.map((user) => [
            user.id,
            {
              name: user.name,
              role: user.role,
              accountType: user.accountType,
              jobTitle: user.jobTitle,
              status: user.status,
              password: "",
            },
          ]),
        ),
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, trash]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setError(null);
    setMessage(null);

    try {
      const parsed = createUserSchema.safeParse(createForm);

      if (!parsed.success) {
        setError(text.validation[getCreateUserValidationCode(parsed.error)]);
        return;
      }

      await apiPost<typeof parsed.data, { id: string }>("/api/users", parsed.data);
      setCreateForm(emptyCreateForm);
      setMessage(text.created);
      await loadUsers();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.createError);
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(userId: string) {
    const draft = drafts[userId];

    if (!draft) {
      return;
    }

    const replacementPassword = draft.password.trim();

    setSavingId(userId);
    setError(null);
    setMessage(null);

    try {
      const payload: Partial<UserDraft> = {
        name: draft.name,
        role: draft.role,
        accountType: draft.accountType,
        jobTitle: draft.jobTitle,
        status: draft.status,
      };

      if (replacementPassword) {
        payload.password = replacementPassword;
      }

      const parsed = updateUserSchema.safeParse(payload);

      if (!parsed.success) {
        setError(text.validation[getCreateUserValidationCode(parsed.error)]);
        return;
      }

      await apiPatch<typeof parsed.data, { ok: true }>(`/api/users/${userId}`, parsed.data);
      setMessage(text.saved);
      await loadUsers();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingId(null);
    }
  }

  async function mutateUser(user: UserRow, action: "trash" | "restore" | "permanent") {
    const confirmation = action === "trash" ? text.confirmTrash : action === "permanent" ? text.confirmPermanent : null;
    if (confirmation && !window.confirm(confirmation)) return;

    setActionId(user.id);
    setError(null);
    setMessage(null);

    try {
      if (action === "trash") await apiDelete<{ ok: true }>(`/api/users/${user.id}`);
      if (action === "restore") await apiPost<Record<string, never>, { ok: true }>(`/api/users/${user.id}/restore`, {});
      if (action === "permanent") await apiDelete<{ ok: true }>(`/api/users/${user.id}/permanent`);
      setMessage(action === "trash" ? text.deleted : action === "restore" ? text.restored : text.permanentlyDeleted);
      await loadUsers();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : text.actionError);
    } finally {
      setActionId(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">{text.title}</h1>
        <p className="text-sm text-muted-foreground">{text.description}</p>
      </header>

      <StatusMessage message={message} error={error} />

      <div className="flex gap-2">
        <Button type="button" variant={trash ? "secondary" : "primary"} onClick={() => setTrash(false)}>{text.activeAccounts}</Button>
        <Button type="button" variant={trash ? "primary" : "secondary"} onClick={() => setTrash(true)}>{text.trash}</Button>
      </div>

      {!trash ? <Card className="p-4">
        <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_150px_140px_150px_auto]" onSubmit={handleCreate}>
          <Input required value={createForm.name} placeholder={text.name} onChange={(event) => setCreateForm({ ...createForm, name: event.target.value })} />
          <Input
            value={createForm.email}
            type="email"
            required
            placeholder={text.email}
            onChange={(event) => setCreateForm({ ...createForm, email: event.target.value })}
          />
          <Input
            value={createForm.password}
            type="password"
            required
            minLength={minimumUserPasswordLength}
            placeholder={text.password}
            onChange={(event) => setCreateForm({ ...createForm, password: event.target.value })}
          />
          <Input required value={createForm.jobTitle} placeholder={text.jobTitle} onChange={(event) => setCreateForm({ ...createForm, jobTitle: event.target.value })} />
          <select
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={createForm.role}
            onChange={(event) => setCreateForm({ ...createForm, role: event.target.value as "admin" | "staff", accountType: event.target.value === "admin" ? "internal" : createForm.accountType })}
          >
            <option value="staff">{text.staff}</option>
            <option value="admin">{text.admin}</option>
          </select>
          <select
            className="h-9 rounded-md border border-border bg-background px-3 text-sm"
            value={createForm.accountType}
            onChange={(event) => setCreateForm({ ...createForm, accountType: event.target.value as "internal" | "external", role: event.target.value === "external" ? "staff" : createForm.role })}
          >
            <option value="internal">{text.internal}</option>
            <option value="external">{text.external}</option>
          </select>
          <Button type="submit" disabled={creating}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {creating ? text.creating : text.create}
          </Button>
        </form>
      </Card> : null}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="text-sm font-medium">{text.accounts}</div>
          <Button type="button" variant="secondary" className="h-8 px-2" onClick={() => void loadUsers()} disabled={loading}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {text.refresh}
          </Button>
        </div>
        {loading ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.loading}</div> : null}
        {!loading && users.length === 0 ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.empty}</div> : null}
        {!loading
          ? users.map((user) => {
              const draft = drafts[user.id];

              return trash ? (
                <div key={user.id} className="grid items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 md:grid-cols-[1fr_1.4fr_150px_160px_auto]">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="min-w-0 text-sm">
                    <div className="truncate">{user.email}</div>
                    <div className="text-xs text-muted-foreground">{text.deletedAt}: {user.deletedAt ? new Date(user.deletedAt).toLocaleString() : "-"}</div>
                  </div>
                  <div className="text-sm">{user.jobTitle}</div>
                  <div className="text-sm text-muted-foreground">{user.accountType === "internal" ? text.internal : text.external}</div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" className="h-8 w-8 p-0" title={text.restore} disabled={actionId === user.id} onClick={() => void mutateUser(user, "restore")}>
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button type="button" variant="secondary" className="h-8 w-8 p-0 text-destructive" title={text.permanent} disabled={actionId === user.id} onClick={() => void mutateUser(user, "permanent")}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={user.id} className="grid gap-3 border-b border-border px-4 py-3 last:border-b-0 xl:grid-cols-[1fr_1.4fr_150px_130px_140px_120px_160px_auto_auto]">
                  <Input value={draft?.name ?? user.name} onChange={(event) => updateDraft(user.id, { name: event.target.value })} aria-label={text.name} />
                  <div className="min-w-0 text-sm">
                    <div className="truncate font-medium">{user.email}</div>
                    <div className="text-xs text-muted-foreground">{new Date(user.createdAt).toLocaleDateString()}</div>
                  </div>
                  <Input value={draft?.jobTitle ?? user.jobTitle} placeholder={text.jobTitle} onChange={(event) => updateDraft(user.id, { jobTitle: event.target.value })} />
                  <select
                    className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                    value={draft?.role ?? user.role}
                    onChange={(event) => updateDraft(user.id, { role: event.target.value as "admin" | "staff", accountType: event.target.value === "admin" ? "internal" : (draft?.accountType ?? user.accountType) })}
                  >
                    <option value="staff">{text.staff}</option>
                    <option value="admin">{text.admin}</option>
                  </select>
                  <select
                    className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                    value={draft?.accountType ?? user.accountType}
                    onChange={(event) => updateDraft(user.id, { accountType: event.target.value as "internal" | "external", role: event.target.value === "external" ? "staff" : (draft?.role ?? user.role) })}
                  >
                    <option value="internal">{text.internal}</option>
                    <option value="external">{text.external}</option>
                  </select>
                  <select
                    className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                    value={draft?.status ?? user.status}
                    onChange={(event) => updateDraft(user.id, { status: event.target.value as "active" | "disabled" })}
                  >
                    <option value="active">{text.active}</option>
                    <option value="disabled">{text.disabled}</option>
                  </select>
                  <Input
                    type="password"
                    minLength={minimumUserPasswordLength}
                    value={draft?.password ?? ""}
                    placeholder={text.newPassword}
                    onChange={(event) => updateDraft(user.id, { password: event.target.value })}
                  />
                  <Button type="button" variant="secondary" onClick={() => void handleSave(user.id)} disabled={savingId === user.id}>
                    <Save className="h-4 w-4" aria-hidden="true" />
                    {savingId === user.id ? text.saving : text.save}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-9 w-9 p-0 text-destructive"
                    title={currentUser?.id === user.id ? text.cannotDeleteSelf : text.delete}
                    disabled={actionId === user.id || currentUser?.id === user.id}
                    onClick={() => void mutateUser(user, "trash")}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              );
            })
          : null}
      </Card>
    </section>
  );

  function updateDraft(userId: string, patch: Partial<UserDraft>) {
    setDrafts((current) => ({
      ...current,
      [userId]: {
        ...(current[userId] ?? { name: "", role: "staff", accountType: "internal", jobTitle: "Staff", status: "active", password: "" }),
        ...patch,
      },
    }));
  }
}

function StatusMessage({ message, error }: { message: string | null; error: string | null }) {
  if (!message && !error) {
    return null;
  }

  return (
    <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
      {error ?? message}
    </div>
  );
}

const en = {
  title: "Users",
  description: "Create and maintain administrators, internal staff, and external collaborators.",
  accounts: "Accounts",
  activeAccounts: "Accounts",
  trash: "Recycle bin",
  name: "Name",
  email: "Email",
  password: "Temporary password",
  newPassword: "New password",
  jobTitle: "Job title",
  staff: "Staff",
  admin: "Admin",
  internal: "Internal",
  external: "External collaborator",
  active: "Active",
  disabled: "Disabled",
  create: "Create",
  creating: "Creating...",
  save: "Save",
  saving: "Saving...",
  refresh: "Refresh",
  loading: "Loading users...",
  empty: "No users yet.",
  created: "User created.",
  saved: "User saved.",
  deleted: "User moved to the recycle bin.",
  restored: "User restored and kept disabled.",
  permanentlyDeleted: "User permanently deleted.",
  delete: "Move to recycle bin",
  restore: "Restore",
  permanent: "Delete permanently",
  deletedAt: "Deleted",
  cannotDeleteSelf: "You cannot delete your own account",
  confirmTrash: "Move this user to the recycle bin? Their sessions will end immediately.",
  confirmPermanent: "Permanently delete this user? This cannot be undone.",
  actionError: "Unable to update user.",
  loadError: "Unable to load users.",
  createError: "Unable to create user.",
  saveError: "Unable to save user.",
  validation: {
    name_required: "Name is required.",
    email_invalid: "Enter a valid email address.",
    password_too_short: `Password must be at least ${minimumUserPasswordLength} characters.`,
    job_title_required: "Job title is required.",
    role_invalid: "Select a valid role.",
    account_type_invalid: "Select a valid account type.",
    status_invalid: "Select a valid status.",
    invalid_payload: "Check the account details and try again.",
  } satisfies Record<CreateUserValidationCode, string>,
};

const zh: typeof en = {
  title: "用户",
  description: "创建和维护管理员、公司员工及外部协作账号。",
  accounts: "账号列表",
  activeAccounts: "账号",
  trash: "回收站",
  name: "姓名",
  email: "邮箱",
  password: "临时密码",
  newPassword: "新密码",
  jobTitle: "岗位/身份",
  staff: "员工",
  admin: "管理员",
  internal: "公司内部",
  external: "外部协作",
  active: "启用",
  disabled: "停用",
  create: "创建",
  creating: "创建中...",
  save: "保存",
  saving: "保存中...",
  refresh: "刷新",
  loading: "正在加载用户...",
  empty: "暂无用户。",
  created: "用户已创建。",
  saved: "用户已保存。",
  deleted: "用户已移入回收站。",
  restored: "用户已恢复并保持停用状态。",
  permanentlyDeleted: "用户已最终删除。",
  delete: "移入回收站",
  restore: "恢复",
  permanent: "最终删除",
  deletedAt: "删除时间",
  cannotDeleteSelf: "不能删除当前登录账号",
  confirmTrash: "将此用户移入回收站？该账号的登录会话会立即失效。",
  confirmPermanent: "最终删除此用户？此操作无法撤销。",
  actionError: "无法更新用户。",
  loadError: "无法加载用户。",
  createError: "无法创建用户。",
  saveError: "无法保存用户。",
  validation: {
    name_required: "请输入姓名。",
    email_invalid: "请输入有效的邮箱地址。",
    password_too_short: `密码至少需要 ${minimumUserPasswordLength} 个字符。`,
    job_title_required: "请输入岗位或身份。",
    role_invalid: "请选择有效的角色。",
    account_type_invalid: "请选择有效的账号类型。",
    status_invalid: "请选择有效的账号状态。",
    invalid_payload: "请检查账号信息后重试。",
  } satisfies Record<CreateUserValidationCode, string>,
};
